/**
 * Taşeron Kesintileri — Şantiye Seçim Ekranı (Faz 34.53+)
 * Kullanıcı önce şantiye kartını seçer, sonra o şantiyenin kesintilerini görür.
 * SubProgressIndex / SubContractsIndex ile aynı desen.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import { searchMatchTR } from "@/lib/textFormat";
import { toast } from "sonner";
import {
  MapPin, MagnifyingGlass, CaretRight, WarningOctagon,
  CurrencyCircleDollar, ListChecks,
} from "@phosphor-icons/react";

const fmtTL = (n) => new Intl.NumberFormat("tr-TR", {
  style: "currency", currency: "TRY", maximumFractionDigits: 0,
}).format(Number(n) || 0);

export default function SubDeductionsIndex() {
  const { sites } = useSites();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sub-deductions`);
        setRows(data.items || []);
      } catch (e) { toast.error(formatErr(e)); }
    })();
  }, []);

  const bySite = useMemo(() => {
    const map = {};
    for (const r of rows) {
      const sid = r.site_id;
      if (!sid) continue;
      if (!map[sid]) map[sid] = { count: 0, total: 0 };
      map[sid].count += 1;
      map[sid].total += Number(r.total_amount) || 0;
    }
    return map;
  }, [rows]);

  const visible = useMemo(() => {
    const active = (sites || []).filter((s) => s.status !== "arsiv");
    if (!q.trim()) return active;
    return active.filter((s) =>
      searchMatchTR(q, [s.code, s.name, s.city, s.district].filter(Boolean).join(" "))
    );
  }, [sites, q]);

  return (
    <Shell>
      <div className="space-y-6" data-testid="sub-deductions-index-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-rose-600/10 flex items-center justify-center">
              <WarningOctagon size={22} weight="duotone" className="text-rose-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1B4776]">Taşeron Kesintileri</h1>
              <p className="text-sm text-slate-500">
                Kesintileri yönetmek için önce bir şantiye seçin.
              </p>
            </div>
          </div>
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Şantiye ara..." className="pl-9 w-72"
              data-testid="sub-deductions-index-search" />
          </div>
        </div>

        {visible.length === 0 ? (
          <Card className="p-10 text-center text-slate-500">
            {q ? "Arama sonucu bulunamadı." : "Henüz aktif şantiye bulunmuyor."}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visible.map((s) => {
              const c = bySite[s.id] || { count: 0, total: 0 };
              return (
                <Link key={s.id} to={`/taseron/kesintiler/${s.id}`}
                      data-testid={`sub-deductions-index-site-${s.id}`} className="group">
                  <Card className="p-5 hover:border-rose-600 hover:shadow-lg transition-all cursor-pointer border-l-4 border-l-rose-500/40 hover:border-l-rose-600">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-mono font-bold text-[#1B4776] tracking-wider uppercase">
                          {s.code}
                        </div>
                        <div className="mt-1 text-base font-bold text-[#0F172A] group-hover:text-rose-700 transition-colors line-clamp-2">
                          {s.name}
                        </div>
                        {(s.city || s.district) && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
                            <MapPin size={13} weight="fill" className="text-[#1B4776]/60" />
                            <span>{[s.district, s.city].filter(Boolean).join(", ")}</span>
                          </div>
                        )}
                      </div>
                      <div className="p-2 rounded-full bg-slate-50 group-hover:bg-rose-600 transition-colors">
                        <CaretRight size={16} weight="bold"
                          className="text-slate-400 group-hover:text-white transition-colors" />
                      </div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-[11px]">
                      <div className="inline-flex items-center gap-1.5 text-slate-500" title="Kayıt Sayısı">
                        <ListChecks size={13} weight="bold" />
                        <span className="font-bold">{c.count}</span>
                        <span className="uppercase tracking-widest">Kayıt</span>
                      </div>
                      <div className="inline-flex items-center gap-1.5 text-rose-700" title="Toplam Kesinti">
                        <CurrencyCircleDollar size={13} weight="bold" />
                        <span className="font-bold font-mono">{fmtTL(c.total)}</span>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
}
